import {Component, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {ObjectBaseModeComponent} from 'features/core/shared/object/mvs-object-base/object-base-mode.component';
import {MvsObjectNavigationService} from 'features/core/shared/navigation/mvs-object-navigation.service';
import {CpConfigChildTab} from '../../../../../interface/cp-config-child-tab.interface';
import {
    buildCpAssignmentQuery,
    CP_ASSIGNMENT_LEADING_COLUMNS,
    CP_ASSIGNMENT_PERIOD_COLUMNS
} from '../../../../../service/domain/cp-provision-assignment-columns';

/**
 * Base mode component of the ProvisionType object component (layer 2 of the three-layer pattern,
 * PAT §L.2).
 *
 * In-module precedent: `component/object-components/cp-provision-agent-object/view/base/
 * cp-provision-agent-base.component.ts`; its own reference is
 * `features/feature-bm/bm/component/object-components/bm-billing-area-object-component/view/
 * bm-billing-area-base.component.ts` — a component WITHOUT its own template (`template: ''`) that
 * only declares the `navigationItems`. `full` and `side` extend it and render the matching shared
 * component per navigation point. Because the class is never rendered itself it deliberately has
 * no .html/.scss (shape of the reference).
 *
 * THIS LEFT NAVIGATION IS THE MANDATORY PART OF THE COMPLEX CONFIGURATION ENTITY RULE: an object
 * component for a complex entity without left navigation is an architectural error
 * (`fe_core_page` anti-pattern 4).
 *
 * `ObjectBaseModeComponent` subscribes to `?navItem=` / `?sideNavItem=` itself — nothing to
 * rebuild here (G6.5 only applies to direct `ObjectBaseComponent` subclasses).
 *
 * ## Terminology (04-frontend.md §1, decision AX-10)
 * The UI separates two terms that both used to read "Provisionsart":
 *   „Provisionstyp"      = `cp.ProvisionType`          — the top configuration level, THIS object
 *   „Provisionsart"      = `cp.ProvisionTypeComponent` — one commission component of it (K-14)
 *   „Provisionsereignis" = `cp.ProvisionTypeContractEvent` (K-15)
 * „Buchungskomponente" (ACQUISITION/CANCELLATION) is technical and never appears in the UI.
 *
 * UI text is German with real umlauts; identifiers and comments are English.
 */
@Component({
    selector: 'cp-provision-type-base',
    template: '',
    standalone: false
})
export class CpProvisionTypeBaseComponent extends ObjectBaseModeComponent implements OnInit {

    /** navItem action of "Bedingungssätze" — also the jump target of the condition preview. */
    static readonly NAV_CONDITION_SETS = 'conditionSets';

    /**
     * Set to select when the navItem "Bedingungssätze" opens — filled by
     * {@link jumpToConditionSet}, i.e. by "Im Bedingungssatz öffnen" of an assignment tab.
     * ONE-SHOT: reset as soon as the condition-set tab has selected it
     * ({@link onFocusConditionSetConsumed}) or another navItem becomes active
     * ({@link onNavigationItemChange}); otherwise a later plain visit of "Bedingungssätze" would
     * preselect the stale set.
     */
    focusConditionSetId: number | null = null;

    private readonly router = inject(Router);
    private readonly objectNavigationService = inject(MvsObjectNavigationService, {optional: true});

    /**
     * Navigation points of the provision type object — the master form plus the children
     * 04-frontend.md §6, Anforderung §4 and 01-requirement.md §6 list under the navItem
     * `cp.ProvisionType`, in the order given there, plus "Bedingungssätze" from the
     * assignment-condition redesign.
     * `action` (not `route`) is the key matched against `?navItem=`.
     *
     * Two of the thirteen are TWO-LEVEL points and therefore have their own component instead of a
     * plain child table:
     *   `contractEvents` (4)  — Provisionsereignis -> Preisgründe   (K-15 -> K-16, AX-13 = A)
     *   `matrices`       (10) — Provisionsmatrix   -> Matrixzeilen  (K-08 -> K-09)
     * Three more are plain child tables wrapped in a component pre-filter: `matrixAssignments` (9)
     * as §6 prescribes, `calculatedAgents` (11, K-17) for the same reason — its mandatory
     * `targetProvisionTypeComponent` partitions the rule set exactly like K-10's component does —
     * and `componentEligibility` (6, K-18), whose mandatory `provisionTypeComponent` does the same.
     *
     * `conditionSets` ("Bedingungssätze", `cp.ProvisionAssignmentConditionSet` ->
     * `cp.ProvisionAssignmentCondition`) is a two-level point as well and stands directly BEFORE the six assignment tabs that reference the sets, so
     * the reading order is "define the contract groups, then assign results to them".
     *
     * `componentEligibility` follows `eligibility` because that is the evaluation order the engine
     * uses (BR-1): the type-wide contract relevance K-05 runs first, and only on ELIGIBLE is the
     * per-component relevance K-18 evaluated — before K-06 and before K-10.
     *
     * `configCheck` is the per-provision-type check report
     * (`GET /mvsa/cp/configCheck/provisionType/{id}`). It lives here and no longer on the config
     * page: the endpoint is scoped to ONE provision type, so inside this object page it needs no
     * picker at all, and 04 §6 lists it as its last child while its list of config-page top-level
     * navItems does not contain it.
     */
    navigationItems: NavigationItem[] = [
        {
            label: 'Basisdaten',
            action: 'baseData',
            tooltip: 'Stammdaten des Provisionstyps',
            icon: 'fa-solid fa-id-card',
            toggleable: false,
            default: true
        },
        {
            label: 'Gruppen',
            action: 'groups',
            tooltip: 'Beratergruppen dieses Provisionstyps',
            icon: 'fa-solid fa-users',
            toggleable: false,
            default: false
        },
        {
            label: 'Provisionsbasen',
            action: 'provisionBases',
            tooltip: 'Berechnungsbasen der Provision inklusive Berechnungsmodus',
            icon: 'fa-solid fa-calculator',
            toggleable: false,
            default: false
        },
        {
            label: 'Provisionsarten',
            action: 'components',
            tooltip: 'Provisionsarten des Provisionstyps mit Rolle und Reihenfolge',
            icon: 'fa-solid fa-puzzle-piece',
            toggleable: false,
            default: false
        },
        {
            label: 'Provisionsereignisse',
            action: 'contractEvents',
            tooltip: 'Auslösende Vertragsereignisse und ihre Preisgründe',
            icon: 'fa-solid fa-calendar-day',
            toggleable: false,
            default: false
        },
        {
            label: 'Bedingungssätze',
            action: CpProvisionTypeBaseComponent.NAV_CONDITION_SETS,
            tooltip: 'Benannte Bedingungssätze, die festlegen, für welche Verträge eine Zuordnung gilt',
            icon: 'fa-solid fa-sliders',
            toggleable: false,
            default: false
        },
        {
            label: 'Relevanz',
            action: 'eligibility',
            tooltip: 'Welche Verträge für diesen Provisionstyp relevant sind',
            icon: 'fa-solid fa-filter-circle-dollar',
            toggleable: false,
            default: false
        },
        {
            label: 'Komponenten-Eignung',
            action: 'componentEligibility',
            tooltip: 'Welche Provisionsarten je Vorvertrag relevant sind — keine Zeile bedeutet relevant',
            icon: 'fa-solid fa-list-check',
            toggleable: false,
            default: false
        },
        {
            label: 'Basiswert-Zuordnung',
            action: 'contractProvisionBases',
            tooltip: 'Zuordnung der Provisionsbasis je Bedingungssatz',
            icon: 'fa-solid fa-arrow-right-arrow-left',
            toggleable: false,
            default: false
        },
        {
            label: 'Haftungs-Zuordnung',
            action: 'contractLiabilities',
            tooltip: 'Zuordnung der Haftungsart je Bedingungssatz',
            icon: 'fa-solid fa-shield-halved',
            toggleable: false,
            default: false
        },
        {
            label: 'Matrix-Zuordnung',
            action: 'matrixAssignments',
            tooltip: 'Zuordnung der Provisionsmatrix je Provisionsart und Bedingungssatz',
            icon: 'fa-solid fa-diagram-project',
            toggleable: false,
            default: false
        },
        {
            label: 'Matrizen',
            action: 'matrices',
            tooltip: 'Provisionsmatrizen und ihre Matrixzeilen je Berater bzw. Gruppe',
            icon: 'fa-solid fa-table-cells',
            toggleable: false,
            default: false
        },
        {
            label: 'Berechnete Vermittler',
            action: 'calculatedAgents',
            tooltip: 'Regeln für zusätzliche Vermittler aus Bedingungssatz und Trigger-Vermittler',
            icon: 'fa-solid fa-user-plus',
            toggleable: false,
            default: false
        },
        {
            label: 'Buchungsereignisse',
            action: 'transactionEvents',
            tooltip: 'Buchungsereignisse und ihre Vertragsbuchungsarten',
            icon: 'fa-solid fa-bolt',
            toggleable: false,
            default: false
        },
        {
            label: 'Konfigurationsprüfung',
            action: 'configCheck',
            tooltip: 'Prüfbericht CR-001…CR-032 dieses Provisionstyps',
            icon: 'fa-solid fa-clipboard-check',
            toggleable: false,
            default: false
        }
    ];

    // =========================================================================
    // Child descriptors
    // =========================================================================

    /**
     * Descriptors of the plain child lists, keyed by the navigation `action`.
     *
     * They reuse the module interface {@link CpConfigChildTab}, which carries exactly the values a
     * parent-filtered child list needs — the stable widget alias (CONFIG-011), the entity alias,
     * the QL field to filter by, the DTO field to pre-fill on create (CONFIG-012) and the curated
     * column set. The aliases are the ones 04-frontend.md §6 assigns; only their location moved
     * from the config page into this object page.
     *
     * NOT in this map, because they are not plain tables:
     *   `contractEvents` — two-level (K-15 -> K-16), own component
     *   `matrices`       — two-level (K-08 -> K-09), own component
     *   `conditionSets`  — two-level (set -> conditions), own component
     *   `configCheck`    — a report panel, not an entity list
     * `matrixAssignments`, `componentEligibility` and `calculatedAgents` ARE in the map: their
     * components only wrap the very same child table in a component pre-filter.
     *
     * ## The six assignment tables (assignment-condition redesign)
     * K-05, K-06, K-07, K-10, K-17 and K-18 no longer carry the 13 contract dimensions — each row
     * references ONE optional condition set. They therefore share ONE fixed column block
     * (`cp-provision-assignment-columns.ts`): Name, Alias, Bedingungssatz (empty = "Alle Verträge
     * (unbedingt)"), Priorität, the family columns, Gültig ab/bis, Status and the family's result.
     * All six are EXPLICIT-JOIN tables (`qlQuery` + `widgetColumns`): the set has to be joined
     * under a stable alias for the preview, and the positional `fkeyt_e_<N>` aliases of
     * `resolve_fkey_texts` all shifted when eleven relations left the entities. With
     * `conditionSetPreview` a selected row shows the conditions of its set read-only below the
     * table, with the jump into "Bedingungssätze".
     *
     * The plain tables (groups, provision bases, components, transaction events) keep the
     * `defaultFormFields` path unchanged — see {@link CpConfigChildTab.defaultFormFields} for the
     * id rules of that path.
     */
    readonly childTabs: { [action: string]: CpConfigChildTab } = {
        /**
         * K-03. Without a curated set the table renders EVERY projected attribute (id, audit
         * fields, colour, the raw parent FK). The entity has exactly three business attributes,
         * so the whitelist is the complete business row; the parent `provisionType` is omitted
         * because the list IS filtered on it and the column would be constant.
         *
         * The ids are `e.`-PREFIXED because this is a QL widget: `QlDtoFormService:189` builds
         * every form-field id from `QlDataMetaMember.getFullAttributeName()` = `<as>.<attribute>`,
         * and `WidgetService.addDefaultFormFieldsToLayout` matches `defaultFormFields[i] ===
         * formField.id` EXACTLY, with no fallback — an un-prefixed `name` matches nothing and the
         * table renders ZERO columns. See {@link CpConfigChildTab.defaultFormFields} and the
         * live-verified `hb-config.page.ts:145-170`.
         */
        groups: {
            alias: 'config.cp.ProvisionTypeGroup.table',
            label: 'Gruppen',
            icon: 'fa-solid fa-users',
            entity: 'cp.ProvisionTypeGroup',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            defaultFormFields: [
                'e.name',
                'e.alias',
                'e.entityStatus'
            ]
        },
        /**
         * K-04. `baseCalculationMode` (E7, DIFFERENCE/FULL) is listed explicitly so the field that
         * decides whether an event provisions the delta or the full amount is visible in the list
         * and not only in the detail form (04 §6, entry 2).
         *
         * `contractReference` (01-requirement.md §4, OWN/FORMER) sits DIRECTLY next to it, because
         * the two are not independent: FD-2 makes `FORMER` demand `baseCalculationMode = FULL`, and
         * the combination FORMER + DIFFERENCE is refused on the write path and reported by the
         * ConfigCheck. Seeing one without the other in the list would hide exactly the pair a
         * caseworker has to get right. Label ("Vertragsbezug") and the German value texts ("Eigener
         * Vertrag" / "Vorvertrag") come from the backend `@UiFieldInfo`/`@UiEnumValueInfo`
         * metadata, as does the default OWN — nothing about them is declared in the frontend.
         */
        provisionBases: {
            alias: 'config.cp.ProvisionTypeProvisionBase.table',
            label: 'Provisionsbasen',
            icon: 'fa-solid fa-calculator',
            entity: 'cp.ProvisionTypeProvisionBase',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            defaultFormFields: [
                'name',
                'alias',
                'priceReference',
                'baseCalculationMode',
                'contractReference',
                'baseLogicDtoName',
                'maxTermMonths',
                'entityStatus'
            ]
        },
        /**
         * K-14, UI term "Provisionsart". Columns exactly as 04 §6 entry 3 prescribes: Name, Alias,
         * Rolle, `orderPriority`, Periode, `reservePercentageOverwrite`.
         */
        components: {
            alias: 'config.cp.ProvisionTypeComponent.table',
            label: 'Provisionsarten',
            icon: 'fa-solid fa-puzzle-piece',
            entity: 'cp.ProvisionTypeComponent',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            defaultFormFields: [
                'name',
                'alias',
                'agentRole',
                'orderPriority',
                'startDate',
                'endDate',
                'reservePercentageOverwrite',
                'entityStatus'
            ]
        },
        /**
         * K-05 — the type-wide contract relevance. Result column `eligibilityResult` ("Relevanz").
         * Family column headers follow the backend `@UiFieldInfo` labels, so the table and the
         * detail dialog name the same field the same way.
         */
        eligibility: {
            alias: 'config.cp.ProvisionTypeContractEligibility.table',
            label: 'Relevanz',
            icon: 'fa-solid fa-filter-circle-dollar',
            entity: 'cp.ProvisionTypeContractEligibility',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            conditionSetPreview: true,
            qlQuery: buildCpAssignmentQuery('config.cp.ProvisionTypeContractEligibility',
                'cp.ProvisionTypeContractEligibility'),
            widgetColumns: [...CP_ASSIGNMENT_LEADING_COLUMNS],
            widgetColumnsTrailing: [
                ...CP_ASSIGNMENT_PERIOD_COLUMNS,
                {field: 'e.eligibilityResult', label: 'Relevanz'}
            ]
        },
        /**
         * K-18 — the per-component relevance rules (01-requirement.md §6). Same column block as
         * K-05 plus the mandatory commission component, which partitions the rule set and is what
         * the pre-filter above the table selects. The result column stays `eligibilityResult`
         * ("Eignung"); K-18 deliberately REUSES `ProvisionEligibilityResult`.
         *
         * Two semantics differ from every sibling and are stated in the tab itself rather than
         * encoded here, because neither is a column:
         *   BR-2  — no row for a component means the component IS eligible (the inverse of K-05)
         *   §12.7 — the verdict is formed PER FORMER CONTRACT, so the former-contract conditions of
         *           the set read the former contract under evaluation, not the main one
         *
         * Rendered through {@link CpProvisionTypeComponentEligibilityComponent}, which only wraps
         * this very child table in the commission-component pre-filter.
         */
        componentEligibility: {
            alias: 'config.cp.ProvisionTypeComponentEligibility.table',
            label: 'Komponenten-Eignung',
            icon: 'fa-solid fa-list-check',
            entity: 'cp.ProvisionTypeComponentEligibility',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            conditionSetPreview: true,
            qlQuery: buildCpAssignmentQuery('config.cp.ProvisionTypeComponentEligibility',
                'cp.ProvisionTypeComponentEligibility',
                [{relation: 'provisionTypeComponent', as: 'ptc'}]),
            widgetColumns: [
                ...CP_ASSIGNMENT_LEADING_COLUMNS,
                {field: 'ptc.name', label: 'Provisionskomponente'}
            ],
            widgetColumnsTrailing: [
                ...CP_ASSIGNMENT_PERIOD_COLUMNS,
                {field: 'e.eligibilityResult', label: 'Eignung'}
            ]
        },
        /**
         * K-06 — result column "Basiswert".
         *
         * The commission component is OPTIONAL here: an EMPTY cell is not a missing value but the
         * statement "gilt typweit" — the historic behaviour and still the default. Specific and
         * type-wide rows form ONE list ordered solely by `orderPriority`, id; there is no implicit
         * "specific beats type-wide" (01-requirement.md §8), which is precisely why the column has
         * to be visible. For that same reason this tab gets NO component pre-filter: filtering on
         * one component would hide the type-wide rows, which are part of that component's rule set.
         */
        contractProvisionBases: {
            alias: 'config.cp.ProvisionTypeContractProvisionBase.table',
            label: 'Basiswert-Zuordnung',
            icon: 'fa-solid fa-arrow-right-arrow-left',
            entity: 'cp.ProvisionTypeContractProvisionBase',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            conditionSetPreview: true,
            qlQuery: buildCpAssignmentQuery('config.cp.ProvisionTypeContractProvisionBase',
                'cp.ProvisionTypeContractProvisionBase',
                [
                    {relation: 'provisionTypeComponent', as: 'ptc'},
                    {relation: 'provisionBase', as: 'pb'}
                ]),
            widgetColumns: [
                ...CP_ASSIGNMENT_LEADING_COLUMNS,
                {field: 'ptc.name', label: 'Provisionskomponente'}
            ],
            widgetColumnsTrailing: [
                ...CP_ASSIGNMENT_PERIOD_COLUMNS,
                {field: 'pb.name', label: 'Basiswert'}
            ]
        },
        /** K-07 — result column "Haftungsart". */
        contractLiabilities: {
            alias: 'config.cp.ProvisionTypeContractLiability.table',
            label: 'Haftungs-Zuordnung',
            icon: 'fa-solid fa-shield-halved',
            entity: 'cp.ProvisionTypeContractLiability',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            conditionSetPreview: true,
            qlQuery: buildCpAssignmentQuery('config.cp.ProvisionTypeContractLiability',
                'cp.ProvisionTypeContractLiability',
                [{relation: 'liabilityType', as: 'lt'}]),
            widgetColumns: [...CP_ASSIGNMENT_LEADING_COLUMNS],
            widgetColumnsTrailing: [
                ...CP_ASSIGNMENT_PERIOD_COLUMNS,
                {field: 'lt.name', label: 'Haftungsart'}
            ]
        },
        /**
         * K-10. Owned by `cp.ProvisionType` (mandatory FK `provisionTypeDtoId`) and merely
         * REFERENCING a matrix as its assignment RESULT — exactly like `contractProvisionBases`
         * references a provision base and `contractLiabilities` a liability type. It therefore
         * belongs here and not under the matrix, which is also how 04 §6 lists it.
         *
         * Two columns beyond the sibling tables (04 §6 entry 8):
         *   "Provisionskomponente" — the mandatory commission component whose matrix this row resolves;
         *       leading, right after the priority, because it partitions the rule set
         *   "Haftungsart (Überschreibung)" — the ABSOLUTE liability override that beats both the
         *       K-07 hit and the K-07 no-match; trailing, next to the matrix it accompanies
         */
        matrixAssignments: {
            alias: 'config.cp.ProvisionTypeMatrixAssignment.table',
            label: 'Matrix-Zuordnung',
            icon: 'fa-solid fa-diagram-project',
            entity: 'cp.ProvisionTypeMatrixAssignment',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            conditionSetPreview: true,
            qlQuery: buildCpAssignmentQuery('config.cp.ProvisionTypeMatrixAssignment',
                'cp.ProvisionTypeMatrixAssignment',
                [
                    {relation: 'provisionTypeComponent', as: 'ptc'},
                    {relation: 'provisionTypeMatrix', as: 'ptm'},
                    {relation: 'liabilityTypeOverwrite', as: 'lto'}
                ]),
            widgetColumns: [
                ...CP_ASSIGNMENT_LEADING_COLUMNS,
                {field: 'ptc.name', label: 'Provisionskomponente'}
            ],
            widgetColumnsTrailing: [
                ...CP_ASSIGNMENT_PERIOD_COLUMNS,
                {field: 'ptm.name', label: 'Provisionsmatrix'},
                {field: 'lto.name', label: 'Haftungsart (Überschreibung)'}
            ]
        },
        /**
         * K-17 — the calculated-agent rules (Anforderung §4). Beyond the common block it shows, in
         * the reading order Anforderung §4 prescribes:
         *   TARGET  — "Zielkomponente" leads (like K-10's component: it partitions the rule set and
         *       is what the pre-filter above the table selects), and "Ziel-Vermittler" trails as the
         *       assignment RESULT, exactly where the sibling tables put theirs
         *   TRIGGER — "Auslösender Vermittler", "Auslösende Gruppe" and "Auslösende Vermittlerrolle";
         *       all three NULL = wildcard, agent AND group together mean AND
         *
         * `name` and `alias` matter doubly here: `(provisionType, alias)` is the business key (D-4)
         * and the alias is what the engine messages M-043/M-044/M-045 name the rule by.
         *
         * <h2>Why the agent names need the two-hop join</h2>
         * `resolve_fkey_texts` emits a join only for targets that BOTH implement
         * `EntityNameGet`/`EntityCalculatedName` AND declare the name field themselves, and it never
         * reaches two hops. `cp.ProvisionAgent` declares no name — its display name is a derived
         * getter over `am.Agent` (`ProvisionAgent.java:265-272`, "the fix belongs in the query and in
         * the view"). So "Ziel-Vermittler" and "Auslösender Vermittler" join `agent` one hop further.
         */
        calculatedAgents: {
            alias: 'config.cp.ProvisionTypeCalculatedAgent.table',
            label: 'Berechnete Vermittler',
            icon: 'fa-solid fa-user-plus',
            entity: 'cp.ProvisionTypeCalculatedAgent',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            conditionSetPreview: true,
            qlQuery: buildCpAssignmentQuery('config.cp.ProvisionTypeCalculatedAgent',
                'cp.ProvisionTypeCalculatedAgent',
                [
                    {relation: 'targetProvisionTypeComponent', as: 'tptc'},
                    {relation: 'triggerProvisionTypeGroup', as: 'gptg'}
                ],
                [
                    // TARGET — the assignment result. Two hops: cp.ProvisionAgent carries no
                    // name, am.Agent does (Agent.java:233, implements EntityName).
                    {
                        type: 'entity', name: 'targetProvisionAgent', as: 'tpa',
                        joinType: 'left', fields: [{name: 'id'}],
                        joins: [
                            {
                                type: 'entity', name: 'agent', as: 'tpaa',
                                joinType: 'left', fields: [{name: 'name'}]
                            }
                        ]
                    },
                    // TRIGGER — same two-hop shape for the agent.
                    {
                        type: 'entity', name: 'triggerProvisionAgent', as: 'gpa',
                        joinType: 'left', fields: [{name: 'id'}],
                        joins: [
                            {
                                type: 'entity', name: 'agent', as: 'gpaa',
                                joinType: 'left', fields: [{name: 'name'}]
                            }
                        ]
                    }
                ]),
            widgetColumns: [
                ...CP_ASSIGNMENT_LEADING_COLUMNS,
                {field: 'tptc.name', label: 'Zielkomponente'},
                {field: 'gpaa.name', label: 'Auslösender Vermittler'},
                {field: 'gptg.name', label: 'Auslösende Gruppe'},
                {field: 'e.triggerAgentRole', label: 'Auslösende Vermittlerrolle'}
            ],
            widgetColumnsTrailing: [
                ...CP_ASSIGNMENT_PERIOD_COLUMNS,
                {field: 'tpaa.name', label: 'Ziel-Vermittler'}
            ]
        },
        /**
         * K-13. The two business-bearing fields are the booking event and the contract-side
         * booking type it books with (`ProvisionTypeTransactionEvent.java:120,143`); everything
         * else on the row is technical. `transactionEvent` is an `@Enumerated` attribute of the
         * root, so it is a plain `e.` column.
         *
         * `contractTransactionType` is a resolvable `@ManyToOne`, so its TEXT only exists under
         * the POSITIONAL alias `fkeyt_e_<N>`. N is DERIVED here, not pinned against a live
         * backend: the entity declares two resolvable relations, `provisionType` (index 0) and
         * `contractTransactionType` (index 1) — both targets implement `EntityName` AND declare
         * `name` themselves. The counting rule is corroborated by `cp.ProvisionTypeMatrixAgent`,
         * whose uncurated table showed exactly three `Name` columns for its three resolvable
         * relations while the unresolvable `provisionAgent` consumed no index
         * (13-phase1-ergebnis-und-auflagen.md §10). If the index is nevertheless wrong the column
         * is simply absent — a miss adds no column, it does not break the table — so this is
         * strictly better than omitting it. VERIFY against live QL form metadata
         * (`POST /mvsa/core/ql/query`, `includeForm=true`) when a backend is available.
         *
         * Known cosmetic limitation: the `fkeyt_e_1.name` column inherits the generic header
         * "Name". `hb-config.page.ts` fixes this with the `defaultFormFieldLabels` widget param;
         * wiring that through {@link CpConfigChildTab} is a separate, structural change.
         */
        transactionEvents: {
            alias: 'config.cp.ProvisionTypeTransactionEvent.table',
            label: 'Buchungsereignisse',
            icon: 'fa-solid fa-bolt',
            entity: 'cp.ProvisionTypeTransactionEvent',
            fkFilterField: 'e.provisionTypeId',
            fkCreateField: 'provisionTypeDtoId',
            defaultFormFields: [
                'e.transactionEvent',
                'fkeyt_e_1.name',
                'e.entityStatus'
            ]
        }
    };

    ngOnInit() {
        super.ngOnInit();
        this.initialized = true;
    }

    /**
     * "Im Bedingungssatz öffnen" of an assignment tab: switch to the navItem "Bedingungssätze"
     * with that set selected.
     *
     * On a normal page the switch is applied locally AND written to the URL — the same
     * `?navItem=` / `?sideNavItem=` a click in the left navigation writes, so the navigation
     * highlights it and a reload restores it.
     *
     * Inside an object-navigation overlay the URL belongs to the MAIN page behind the overlay and
     * must not be touched (TM-1000001554, `ObjectBaseModeComponent`). The switch then goes through
     * the overlay tab channel `MvsObjectNavigationService.setOverlayNavItem`, exactly like a topbar
     * click; `ObjectBaseModeComponent.handleOverlayNavItemChange` applies it and emits
     * `activeNavigationItemChange`. Should the channel not reach this instance (it is not the
     * overlay's own object), the switch is applied locally and emitted here.
     */
    jumpToConditionSet(conditionSetId: number): void {

        const target = this.navigationItems?.find(
            item => item.action === CpProvisionTypeBaseComponent.NAV_CONDITION_SETS);
        if (!target || !conditionSetId) {
            return;
        }

        this.focusConditionSetId = conditionSetId;

        if (this.objectNavigationService?.getActiveOverlay()) {
            this.objectNavigationService.setOverlayNavItem(target.action);
            if (this.activeNavigationItem !== target) {
                this.activeNavigationItem = target;
                this.activeNavigationItemChange.emit(target);
            }
            return;
        }

        this.activeNavigationItem = target;

        const param = this.uiMode === 'side' || this.uiMode === 'mini-side' ? 'sideNavItem' : 'navItem';
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {[param]: target.action},
            queryParamsHandling: 'merge'
        });
    }

    /** The condition-set tab selected {@link focusConditionSetId} — the jump is consumed. */
    onFocusConditionSetConsumed(): void {
        this.focusConditionSetId = null;
    }

    /** Leaving "Bedingungssätze" drops a pending jump target, see {@link focusConditionSetId}. */
    override onNavigationItemChange(navigationItem: NavigationItem): void {
        super.onNavigationItemChange(navigationItem);
        if (navigationItem?.action !== CpProvisionTypeBaseComponent.NAV_CONDITION_SETS) {
            this.focusConditionSetId = null;
        }
    }

}
